/** SSO sign-in buttons — flag-gated dark until the backend flow ships.
 *
 *  The backend was deferred to the auth-hardening lane, so nothing renders
 *  unless VITE_SSO_PROVIDERS is set (e.g. "google,microsoft"). When it is,
 *  each provider renders as a link into the assumed backend contract:
 *
 *      GET /api/auth/sso/{provider}/start?next={path}
 *
 *  (redirect flow: the API sends the browser to the IdP and finishes the
 *  session on the callback). If the auth lane lands a different contract,
 *  this is the only file to touch.
 */

type Provider = { key: string; label: string; mark: JSX.Element };

const PROVIDERS: Record<string, Provider> = {
  google: {
    key: "google",
    label: "Continue with Google",
    mark: (
      <svg viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62Z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
        <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
      </svg>
    ),
  },
  microsoft: {
    key: "microsoft",
    label: "Continue with Microsoft",
    mark: (
      <svg viewBox="0 0 18 18" aria-hidden="true">
        <rect x="1" y="1" width="7.5" height="7.5" fill="#F25022" />
        <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00" />
        <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF" />
        <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900" />
      </svg>
    ),
  },
};

export function SsoButtons({ next }: { next: string }) {
  const enabled = (import.meta.env.VITE_SSO_PROVIDERS || "")
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p in PROVIDERS);

  if (enabled.length === 0) return null;

  return (
    <>
      <div className="sso-group">
        {enabled.map((key) => {
          const p = PROVIDERS[key];
          return (
            <a
              key={p.key}
              className="btn btn-secondary btn-block sso-btn"
              href={`/api/auth/sso/${p.key}/start?next=${encodeURIComponent(next)}`}
            >
              <span className="sso-mark">{p.mark}</span>
              {p.label}
            </a>
          );
        })}
      </div>
      <div className="auth-divider" aria-hidden="true"><span>or sign in with email</span></div>
    </>
  );
}
